import { cn } from "../lib/cn";

const SIZES = {
  24: "size-6 text-[10px]",
  32: "size-8 text-caption",
  40: "size-10 text-small",
  64: "size-16 text-heading",
} as const;

/** One or two initials: the first character of a Chinese or Japanese name, the first letters of two words otherwise. */
export function initials(name: string): string {
  const trimmed = name.trim();
  if (trimmed === "") return "";
  if (/^[\p{Script=Han}\p{Script=Hiragana}\p{Script=Katakana}\p{Script=Hangul}]/u.test(trimmed))
    return Array.from(trimmed)[0] ?? "";
  const words = trimmed.split(/\s+/).filter((word) => /\p{L}/u.test(word));
  return words
    .slice(0, 2)
    .map((word) => Array.from(word.replace(/^[^\p{L}]+/u, ""))[0] ?? "")
    .join("")
    .toUpperCase();
}

/**
 * A person, round, or an enterprise, square (docs/design/meridian.md): their picture, or initials on a tint. It is
 * decorative, since the name always shows beside it.
 */
export function Avatar({
  name,
  kind = "person",
  size = 32,
  src,
  className,
}: {
  name: string;
  kind?: "person" | "enterprise";
  size?: keyof typeof SIZES;
  src?: string | null | undefined;
  className?: string;
}) {
  const shape = kind === "person" ? "rounded-full" : "rounded-[25%]";
  if (src !== null && src !== undefined && src !== "")
    return <img src={src} alt="" className={cn(SIZES[size], shape, "shrink-0 object-cover", className)} />;
  return (
    <span
      aria-hidden="true"
      className={cn(
        SIZES[size],
        shape,
        "inline-flex shrink-0 items-center justify-center font-semibold select-none",
        kind === "person"
          ? "bg-brand-100 text-brand-900"
          : "bg-avatar-enterprise-bg text-avatar-enterprise-fg",
        className,
      )}
    >
      {initials(name)}
    </span>
  );
}
